import { ActivityFilterModel, ActivityOrderBy } from '@gripp/shared-logic';
import { useTranslation } from 'react-i18next';
import { Pressable, Text, View } from 'react-native';
import { IssueCardData } from '../activity/issueCard';
import { IssueCardCompact } from '../activity/issueCardCompact';
import { tokens } from '../crop-circle/tokens';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

type PinnedActivitiesProps = {
  assetId: string;
  pinnedActivities: IssueCardData[];
  showAll?: boolean;
  onViewAllPress?: () => void;
  onActivityPress?: (activityId: string) => void;
};

const pinnedFilter = new ActivityFilterModel({
  orderBy: ActivityOrderBy.MostRecentUpdated,
});

export const PinnedActivities = (props: PinnedActivitiesProps) => {
  return <PinnedActivitiesBase {...props} />;
};

const PinnedActivitiesBase = ({
  pinnedActivities,
  showAll = false,
  onViewAllPress,
  onActivityPress,
}: PinnedActivitiesProps) => {
  const { t } = useTranslation();

  if (!pinnedActivities || pinnedActivities.length === 0) {
    return null;
  }

  const displayedActivities = showAll
    ? pinnedActivities
    : pinnedActivities.slice(0, 3);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <Text style={[tokens.typography.label.largeEmphasised, styles.title]}>
          {t('asset.pinnedActivity')}
        </Text>
        {!showAll && pinnedActivities.length > 3 && (
          <Pressable onPress={onViewAllPress}>
            <Text style={styles.viewAllText}>{t('asset.viewAll')}</Text>
          </Pressable>
        )}
      </View>

      <View style={styles.activitiesList}>
        {displayedActivities.map((activity) => (
          <Pressable
            key={activity.id}
            onPress={() => onActivityPress?.(activity.id)}
          >
            <IssueCardCompact
              account={null}
              activity={activity}
              filter={pinnedFilter}
            />
          </Pressable>
        ))}
      </View>
    </View>
  );
};

const styles = themedStyleSheet((role) => ({
  container: {
    backgroundColor: role.canvas,
    padding: tokens.spacing.lg,
    borderRadius: tokens.borderRadius.xl,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: 0,
    marginBottom: tokens.spacing.sm,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: tokens.spacing.lg,
  },
  title: {
    fontWeight: tokens.typography.label.largeEmphasised.fontWeight,
    color: role.textStrong,
  },
  viewAllText: {
    ...tokens.typography.body.small,
    color: role.textSoft,
  },
  activitiesList: {
    flexDirection: 'column',
    gap: tokens.spacing.xs,
  },
}));
